import React from 'react';
import { Root, Value, Portal } from '@radix-ui/react-select';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import {
  SelectRoot,
  SelectTrigger,
  TriggerContent,
  SelectIcon,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
} from './styles';

export interface SelectOption {
  label: string | number;
  value: string | number;
  className?: string;
  disabled?: boolean;
}

export interface RadixSelectProps {
  options: SelectOption[];
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  className?: string;
  'aria-label'?: string;
  prefixIcon?: React.ReactNode;
  portalContainer?: HTMLElement | null;
}

/** The element the embed's styles are scoped to; see `index.js`, which namespaces every rule under it. */
const EMBED_ROOT_ID = 'kouto-embed-root';

// Using styled-components following platform patterns

export const RadixSelect: React.FC<RadixSelectProps> = ({
  options,
  value,
  defaultValue,
  placeholder,
  disabled,
  onValueChange,
  className,
  'aria-label': ariaLabel,
  prefixIcon,
  portalContainer,
}) => {
  const selectedValue = value !== undefined ? value.toString() : undefined;
  const stringDefaultValue =
    defaultValue !== undefined ? defaultValue.toString() : undefined;

  return (
    <SelectRoot>
      <Root
        value={selectedValue}
        defaultValue={stringDefaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger className={className} aria-label={ariaLabel}>
          <TriggerContent>
            {prefixIcon && prefixIcon}
            <Value placeholder={placeholder} />
          </TriggerContent>
          <SelectIcon>
            <ChevronDownIcon />
          </SelectIcon>
        </SelectTrigger>

        {/* The list is portalled out of the field, so it goes into the embed's root unless told otherwise:
            elsewhere in the page it sits outside every rule the embed's styles are namespaced under, and
            renders as bare, unstyled text over whatever is behind it. */}
        <Portal
          container={
            portalContainer ||
            document.getElementById(EMBED_ROOT_ID) ||
            undefined
          }
        >
          <SelectContent
            side="bottom"
            align="start"
            sideOffset={2}
            avoidCollisions={false}
            position="popper"
          >
            <SelectViewport>
              {options.map((option, i) => (
                <SelectItem
                  key={option.value}
                  value={option.value.toString()}
                  className={option.className}
                  disabled={option.disabled}
                  id={`react-select-2-option-${i}`}
                >
                  <SelectItemText>{option.label}</SelectItemText>
                </SelectItem>
              ))}
            </SelectViewport>
          </SelectContent>
        </Portal>
      </Root>
    </SelectRoot>
  );
};

RadixSelect.displayName = 'RadixSelect';

export default RadixSelect;
